import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { GoogleMap, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api';
import toast from 'react-hot-toast';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../config/env';
import {
  CreateClinicBranchPayload,
  createClinicBranch,
} from '../../services/MasterAdmin/doctorClinicTimeslotService';
import {
  DEFAULT_MAP_CENTER,
  MAP_LIBRARIES,
  extractPincodeFromAddressComponents,
  hasValidClinicCoordinates,
  sanitizePincodeInput,
} from '../../utils/clinicBranchMap';

interface DoctorClinicBranchModalProps {
  open: boolean;
  vendorId: string;
  onClose: () => void;
  onSaved: () => void;
}

type LatLng = { lat: number; lng: number };

const emptyForm: CreateClinicBranchPayload = {
  clinicName: '',
  address: '',
  pincode: '',
  latitude: DEFAULT_MAP_CENTER.lat,
  longitude: DEFAULT_MAP_CENTER.lng,
};

const DoctorClinicBranchModal: React.FC<DoctorClinicBranchModalProps> = ({
  open,
  vendorId,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<CreateClinicBranchPayload>(emptyForm);
  const [mapCenter, setMapCenter] = useState<LatLng>(DEFAULT_MAP_CENTER);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState<{ message: string; isError: boolean }>({
    message: '',
    isError: false,
  });

  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
  const isSearchingRef = useRef(false);

  const mapsApiKey = REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
    id: 'ma-clinic-branch-map',
    googleMapsApiKey: mapsApiKey,
    libraries: MAP_LIBRARIES,
  });

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setMapCenter(DEFAULT_MAP_CENTER);
    setError('');
    setLocationStatus({ message: '', isError: false });
    mapInstanceRef.current = null;
    searchBoxRef.current = null;
    isSearchingRef.current = false;
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
  }, [open, resetForm]);

  const reverseGeocode = (lat: number, lng: number) => {
    if (!window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const pin = extractPincodeFromAddressComponents(results[0].address_components);
        const formattedAddress = results[0].formatted_address || '';
        setForm((prev) => ({
          ...prev,
          pincode: sanitizePincodeInput(pin || prev.pincode),
          address: formattedAddress || prev.address,
        }));
      }
    });
  };

  const onMapIdle = () => {
    if (isSearchingRef.current) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    const center = map.getCenter();
    if (!center) return;
    const lat = center.lat();
    const lng = center.lng();
    setMapCenter({ lat, lng });
    setForm((prev) => {
      if (
        Math.abs(lat - Number(prev.latitude)) < 0.0001 &&
        Math.abs(lng - Number(prev.longitude)) < 0.0001
      ) {
        return prev;
      }
      return { ...prev, latitude: lat, longitude: lng };
    });
  };

  const onPlacesChanged = () => {
    const searchBox = searchBoxRef.current;
    if (!searchBox) return;
    const places = searchBox.getPlaces();
    if (!places?.length) return;
    const place = places[0];
    if (!place.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const pos = { lat, lng };

    isSearchingRef.current = true;
    setMapCenter(pos);
    mapInstanceRef.current?.panTo(pos);
    mapInstanceRef.current?.setZoom(17);

    let pincode = '';
    if (Array.isArray(place.address_components)) {
      pincode = extractPincodeFromAddressComponents(place.address_components);
    }
    const formattedAddress = place.formatted_address || place.name || '';

    setForm((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      pincode: sanitizePincodeInput(pincode || prev.pincode),
      address: formattedAddress || prev.address,
    }));
    setLocationStatus({
      message: 'Location selected from search. Address and pincode were updated.',
      isError: false,
    });

    window.setTimeout(() => {
      isSearchingRef.current = false;
    }, 1500);
  };

  const handleMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus({
        message: 'Location is not supported in this browser.',
        isError: true,
      });
      return;
    }
    setMapLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        isSearchingRef.current = true;
        setMapCenter(newPos);
        mapInstanceRef.current?.panTo(newPos);
        mapInstanceRef.current?.setZoom(17);
        setForm((prev) => ({
          ...prev,
          latitude: newPos.lat,
          longitude: newPos.lng,
        }));
        reverseGeocode(newPos.lat, newPos.lng);
        setLocationStatus({
          message: 'Location fetched successfully. Fine-tune the pin by moving the map.',
          isError: false,
        });
        setMapLoading(false);
        window.setTimeout(() => {
          isSearchingRef.current = false;
        }, 1500);
      },
      (err) => {
        const denied = err?.code === 1;
        setLocationStatus({
          message: denied
            ? 'Location permission blocked. Allow location for this site in browser settings, then try again.'
            : 'Location unavailable. Enable device location or search on the map.',
          isError: true,
        });
        setMapLoading(false);
      }
    );
  };

  const validate = () => {
    if (!form.clinicName.trim()) return 'Clinic name is required.';
    if (!form.address.trim()) return 'Address is required.';
    if (!/^\d{6}$/.test(form.pincode.trim())) return 'Pincode must be exactly 6 digits.';
    if (!hasValidClinicCoordinates(form.latitude, form.longitude, form.address)) {
      return 'Please search, use My Location, or move the map to pin the clinic on the map.';
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    try {
      await createClinicBranch(vendorId, {
        clinicName: form.clinicName.trim(),
        address: form.address.trim(),
        pincode: form.pincode.trim(),
        latitude: Number(form.latitude) || 0,
        longitude: Number(form.longitude) || 0,
      });
      toast.success('Clinic location added successfully.');
      onSaved();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to add clinic location.';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-4">
      <style>{`.pac-container { z-index: 999999 !important; }`}</style>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add clinic location</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Search or pin the clinic on the map, then save the location.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto p-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Clinic name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.clinicName}
              onChange={(e) => setForm((p) => ({ ...p, clinicName: e.target.value }))}
              placeholder="e.g. Vedika Clinic – Koramangala"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Full address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              rows={2}
              placeholder="Auto-filled from map search, or type manually"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Pincode <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={form.pincode}
              onChange={(e) =>
                setForm((p) => ({ ...p, pincode: sanitizePincodeInput(e.target.value) }))
              }
              placeholder="6-digit pincode"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500">Auto-filled from map when available.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Map location <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                {isMapLoaded ? (
                  <StandaloneSearchBox
                    onLoad={(ref) => {
                      searchBoxRef.current = ref;
                    }}
                    onPlacesChanged={onPlacesChanged}
                  >
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search location on map"
                        className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </StandaloneSearchBox>
                ) : (
                  <input
                    type="text"
                    disabled
                    placeholder={loadError ? 'Map failed to load' : 'Loading map search...'}
                    className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700"
                  />
                )}
              </div>
              <button
                type="button"
                onClick={handleMyLocation}
                disabled={mapLoading || !isMapLoaded}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
              >
                <MapPinIcon className="h-4 w-4" />
                {mapLoading ? 'Fetching…' : 'My location'}
              </button>
            </div>
          </div>

          {locationStatus.message && (
            <p
              className={`rounded-lg px-3 py-2 text-sm ${
                locationStatus.isError
                  ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
                  : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200'
              }`}
            >
              {locationStatus.message}
            </p>
          )}

          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-600">
            {!mapsApiKey ? (
              <div className="flex h-64 items-center justify-center bg-gray-50 px-4 text-center text-sm text-gray-500 dark:bg-gray-700/50">
                Missing <code className="mx-1 rounded bg-gray-200 px-1 dark:bg-gray-600">REACT_APP_GOOGLE_MAPS_API_KEY</code>{' '}
                in admin panel environment.
              </div>
            ) : isMapLoaded ? (
              <div className="relative h-64 w-full">
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapCenter}
                  zoom={15}
                  onLoad={(map) => {
                    mapInstanceRef.current = map;
                  }}
                  onIdle={onMapIdle}
                  options={{
                    disableDefaultUI: true,
                    clickableIcons: false,
                  }}
                />
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-full flex-col items-center">
                  <span className="mb-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold text-blue-800 shadow-sm">
                    Selected location
                  </span>
                  <MapPinIcon className="h-10 w-10 text-red-500 drop-shadow" />
                </div>
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center bg-gray-50 dark:bg-gray-700/50">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Drag the map to position the pin. Coordinates:{' '}
            {Number(form.latitude).toFixed(5)}, {Number(form.longitude).toFixed(5)}
          </p>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !mapsApiKey}
              className="flex-1 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Add clinic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorClinicBranchModal;
